import { http } from '@/api/client/httpClient';
import { downloadFile, uploadFile } from '@/api/client/download';
import type { PagedResult } from '@/api/types/abp';
import type {
  AddMissingSymbolsResultDto,
  AssetClassDto,
  AssetClassesFiltersDatatableDto,
  CreateAssetClassDto,
  CreateDebtInstrumentDto,
  CreateExecutionVenueDto,
  CreateExecutionVenueResultDto,
  CreateGroupCategoryDto,
  CreatePortfolioDto,
  CreateSymbolDto,
  CreateSymbolsDatatableDto,
  DebtInstrumentDropdownDataDto,
  DebtInstrumentDto,
  DebtSymbolFiltersDto,
  ExecutionVenueDatatableDto,
  ExecutionVenueDto,
  ExecutionVenueLookupDto,
  GroupCategoryDropdownDataDto,
  GroupCategoryDto,
  GroupCategoryFilterDto,
  LoginInfoDatatableFiltersDto,
  LoginInfoDto,
  MissingSymbolDto,
  PortfolioDatatableDto,
  PortfolioDto,
  PortfoliosDropdownDataDto,
  SymbolDropdownDataDto,
  SymbolDto,
  SymbolsUploadResponse,
  UpdateDebtInstrumentDto,
  UpdateExecutionVenueDto,
  UpdateGroupCategoryDto,
  UpdatePortfolioDto,
  UpdateSymbolDto,
} from '@/api/types/onboarding';

// --- TAB 1: Symbols (/api/app/symbols) + MVC /Symbols/* -----------------------
const SYMBOLS = '/api/app/symbols';
export const symbolsApi = {
  getListForDataTable: (input: CreateSymbolsDatatableDto) =>
    http.get<PagedResult<SymbolDto>>(`${SYMBOLS}/for-data-table`, { params: input }).then((r) => r.data),
  getDropdownData: () => http.get<SymbolDropdownDataDto>(`${SYMBOLS}/dropdown-data`).then((r) => r.data),
  getMissingSymbols: () => http.get<MissingSymbolDto[]>(`${SYMBOLS}/missing-symbols`).then((r) => r.data),
  create: (input: CreateSymbolDto) => http.post<SymbolDto>(SYMBOLS, input).then((r) => r.data),
  update: (id: string, input: UpdateSymbolDto) => http.put<SymbolDto>(`${SYMBOLS}/${id}`, input).then((r) => r.data),
  delete: (id: string) => http.delete(`${SYMBOLS}/${id}`).then((r) => r.data),
  addMissingSymbols: (input: CreateSymbolDto[]) =>
    http.post<AddMissingSymbolsResultDto>(`${SYMBOLS}/add-missing-symbols`, input).then((r) => r.data),
  // MVC routes (not proxy).
  downloadInstruments: () => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    return downloadFile('/Symbols/DownloadInstruments', `Instruments ${dd}-${mm}-${today.getFullYear()}.xlsx`);
  },
  uploadSymbols: (file: File) => uploadFile<SymbolsUploadResponse>('/Symbols/UploadSymbols', file),
};

// --- TAB 2: TDI Instruments (/api/app/tdi-instruments) ------------------------
const TDI = '/api/app/tdi-instruments';
export const tdiInstrumentsApi = {
  getListForDataTable: (input: DebtSymbolFiltersDto) =>
    http.get<PagedResult<DebtInstrumentDto>>(`${TDI}/for-data-table`, { params: input }).then((r) => r.data),
  getDropdownData: () => http.get<DebtInstrumentDropdownDataDto>(`${TDI}/dropdown-data`).then((r) => r.data),
  create: (input: CreateDebtInstrumentDto) => http.post<DebtInstrumentDto>(TDI, input).then((r) => r.data),
  update: (id: string, input: UpdateDebtInstrumentDto) =>
    http.put<DebtInstrumentDto>(`${TDI}/${id}`, input).then((r) => r.data),
  delete: (id: string) => http.delete(`${TDI}/${id}`).then((r) => r.data),
};

// --- TAB 3: Asset Classes (/api/app/asset-classes) ----------------------------
const ASSET_CLASSES = '/api/app/asset-classes';
export const assetClassesApi = {
  getListForDataTable: (input: AssetClassesFiltersDatatableDto) =>
    http.get<PagedResult<AssetClassDto>>(`${ASSET_CLASSES}/for-data-table`, { params: input }).then((r) => r.data),
  create: (input: CreateAssetClassDto) => http.post<AssetClassDto>(ASSET_CLASSES, input).then((r) => r.data),
  getParentAssetClasses: () =>
    http.get<AssetClassDto[]>(`${ASSET_CLASSES}/parent-asset-classes`).then((r) => r.data),
};

// --- TAB 4: Execution Venues (/api/app/execution-venues) ----------------------
const EXEC_VENUES = '/api/app/execution-venues';
export const executionVenuesApi = {
  getListForDataTable: (input: ExecutionVenueDatatableDto) =>
    http.get<PagedResult<ExecutionVenueDto>>(`${EXEC_VENUES}/for-data-table`, { params: input }).then((r) => r.data),
  create: (input: CreateExecutionVenueDto) =>
    http.post<CreateExecutionVenueResultDto>(EXEC_VENUES, input).then((r) => r.data),
  confirmCreate: (input: CreateExecutionVenueDto) =>
    http.post<ExecutionVenueDto>(`${EXEC_VENUES}/confirm-create`, input).then((r) => r.data),
  update: (id: string, input: UpdateExecutionVenueDto) =>
    http.put<ExecutionVenueDto>(`${EXEC_VENUES}/${id}`, input).then((r) => r.data),
  delete: (id: string) => http.delete(`${EXEC_VENUES}/${id}`).then((r) => r.data),
  getDropdownData: () => http.get<ExecutionVenueLookupDto>(`${EXEC_VENUES}/dropdown-data`).then((r) => r.data),
};

// --- TAB 5: Group Categories (/api/app/group-categories) ----------------------
const GROUP_CATS = '/api/app/group-categories';
export const groupCategoriesApi = {
  getListForDataTable: (input: GroupCategoryFilterDto) =>
    http.get<PagedResult<GroupCategoryDto>>(`${GROUP_CATS}/for-data-table`, { params: input }).then((r) => r.data),
  create: (input: CreateGroupCategoryDto) => http.post<GroupCategoryDto>(GROUP_CATS, input).then((r) => r.data),
  update: (id: string, input: UpdateGroupCategoryDto) =>
    http.put<GroupCategoryDto>(`${GROUP_CATS}/${id}`, input).then((r) => r.data),
  fetchGroupsFromDataSources: () =>
    http.post<string>(`${GROUP_CATS}/fetch-groups-from-data-sources`, {}).then((r) => r.data),
  getDropdownData: () => http.get<GroupCategoryDropdownDataDto>(`${GROUP_CATS}/dropdown-data`).then((r) => r.data),
};

// --- TAB 6: Login Information (/api/app/login-info) + MVC ----------------------
const LOGIN_INFO = '/api/app/login-info';
export const loginInfoApi = {
  getListForDataTable: (input: LoginInfoDatatableFiltersDto) =>
    http.get<PagedResult<LoginInfoDto>>(`${LOGIN_INFO}/for-data-table`, { params: input }).then((r) => r.data),
  // MVC routes — NOT implemented server-side (will 404); wired per spec.
  downloadTemplate: () => downloadFile('/LoginInformation/DownloadTemplate', 'LoginInformationTemplate.xlsx'),
  uploadExcel: (file: File) => uploadFile<unknown>('/LoginInformation/UploadExcel', file),
};

// --- TAB 7: Portfolios (/api/app/portfolios) ----------------------------------
const PORTFOLIOS = '/api/app/portfolios';
export const portfoliosApi = {
  // GetListForDataTable → GET /for-data-table (ABP trims the GetList prefix). All seven
  // onboarding grids are Get* → GET; DTO binds from the query string.
  getListForDataTable: (input: PortfolioDatatableDto) =>
    http.get<PagedResult<PortfolioDto>>(`${PORTFOLIOS}/for-data-table`, { params: input }).then((r) => r.data),
  create: (input: CreatePortfolioDto) => http.post<PortfolioDto>(PORTFOLIOS, input).then((r) => r.data),
  update: (id: number, input: UpdatePortfolioDto) =>
    http.put<PortfolioDto>(`${PORTFOLIOS}/${id}`, input).then((r) => r.data),
  delete: (id: number) => http.delete(`${PORTFOLIOS}/${id}`).then((r) => r.data),
  getDropdownData: () => http.get<PortfoliosDropdownDataDto>(`${PORTFOLIOS}/dropdown-data`).then((r) => r.data),
};
